import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from 'react-intl';
import latinize from 'latinize';
import {
  Autocomplete,
  Button,
  DataTable,
  DialogContainer,
  EditDialogColumn,
  SelectFieldColumn,
  FontIcon,
  TableHeader,
  TableBody,
  TableRow,
  TableColumn,
  TextField,
} from 'react-md';

import ErrorDisplay from '../ErrorDisplay';
import KebabMenu from '../KebabMenu';
import { invokeBackendApi } from '../../libs/awsLib';
import { nameValueReducer } from '../../utils/reducers';
import LoaderButton from '../LoaderButton';

export default injectIntl(class UsersTab extends Component {
  static propTypes = {
    isAuthenticated: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    displayedAttributes: PropTypes.arrayOf(PropTypes.shape({
      tableHeader: PropTypes.string,
      attributeKey: PropTypes.string.isRequired,
      grow: PropTypes.bool,
      editor: PropTypes.object,
      display: PropTypes.func,
    })).isRequired,
    tabTitle: PropTypes.string,
    group: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      error: '',
      count: 0,
      sortColumn: 1,
      users: [],
      sortedUsers: [],
      isLoading: true,
      isEditSubmitLoading: false,
      currentEdit: null,
      currentEditUser: {},
      currentDelete: null,
      searchField: '',
    };
  }

  componentDidMount() {
    if (!this.props.isAuthenticated) {
      return;
    }

    this.refresh();
  }
  async refresh() {
    try {
      const results = await this.users();
      this.setState({
        isLoading: false,
        users: results.map(u => ({ ...u, attr: u.Attributes.reduce(nameValueReducer, {}) })),
      }, () => this.sort(this.state.sortColumn));
    } catch (e) {
      alert(e);
    }
  }

  users() {
    return invokeBackendApi({ path: '/users', queryParams: { groupName: this.props.group } });
  }

  isCurrentEditSimilar = () => {
    const {
      currentEditUser: {
        // eslint-disable-next-line camelcase
        name, family_name, birthdate, sub,
      },
    } = this.state;
    return this.state.users.find(a =>
      a.attr.name === name &&
      // eslint-disable-next-line camelcase
      a.attr.family_name === family_name &&
      a.attr.birthdate === birthdate &&
      a.attr.sub !== sub) !== undefined;
  };

  isCurrentEditImeiAssigned = () => {
    const {
      currentEditUser: {
        // eslint-disable-next-line camelcase
        'custom:assignedBlackbox': assignedBlackbox,
        sub,
      },
    } = this.state;
    return this.props.group === 'driverGroup' && assignedBlackbox !== '' && assignedBlackbox !== undefined
      && this.state.users.find(a =>
        a.attr['custom:assignedBlackbox'] === assignedBlackbox &&
        // eslint-disable-next-line camelcase
        a.attr.sub !== sub
      ) !== undefined;
  };

  handleOnChange(property, value) {
    const errorOnSimilar = () => {
      const alreadyAssignedImei = this.isCurrentEditImeiAssigned();
      const similar = this.isCurrentEditSimilar();
      const error = similar ? 'similar' : (alreadyAssignedImei ? 'alreadyAssignedImei' : undefined);
      this.setState({ error });
    };


    this.setState({
      currentEditUser: {
        ...this.state.currentEditUser,
        [property]: value,
      },
    }, errorOnSimilar);
  }

  handleSearchFieldChange = (searchField) => {
    this.setState({ searchField });
  }

  handleClickEdit(user) {
    this.setState({ currentEdit: user.Username, currentEditUser: user.attr });
  }

  handleClickOnCancel = () => {
    const sortedUsers = this.state.currentEdit === 0 ? this.state.sortedUsers.slice(1) : this.state.sortedUsers;
    this.setState({
      currentEdit: null, currentEditUser: {}, sortedUsers, error: '',
    });
  }

  handleClickDelete(user) {
    this.setState({ currentDelete: user.Username });
  }

  handleClickNew() {
    if (this.state.currentEdit !== 0) {
      this.setState({ currentEdit: 0, currentEditUser: {}, sortedUsers: [{ Username: 0, attr: {} }, ...this.state.sortedUsers] });
    }
  }

  validateForm() {
    const { currentEditUser } = this.state;
    const isImeiAlreadyAssigned = this.isCurrentEditImeiAssigned();
    const isSimilar = this.isCurrentEditSimilar();
    return (currentEditUser.email || '').length > 0 &&
    (currentEditUser.name || '').length > 0 &&
    (currentEditUser.family_name || '').length > 0 &&
    (currentEditUser['custom:comment'] || '').length <= 200 &&
    !isImeiAlreadyAssigned &&
    (!isSimilar || (isSimilar && (currentEditUser['custom:comment'] || '') !== ''));
  }

  async handleSubmit() {
    this.setState({ isEditSubmitLoading: true, error: '' });
    const { onChange = () => {} } = this.props;
    const { currentEdit, currentEditUser } = this.state;
    const path = currentEdit === 0 ? '/users/' : `/users/${currentEdit}`;
    invokeBackendApi({ path, method: 'POST', body: { group: this.props.group, attributes: currentEditUser } })
      .then(() => {
        this.setState({ isEditSubmitLoading: false, currentEdit: null, currentEditUser: {} });
        this.refresh();
        onChange();
      })
      .catch((e) => {
        this.setState({ isEditSubmitLoading: false, error: e.message });
      });
  }

  hide = () => {
    this.setState({ currentDelete: null });
  };

  confirmDelete = async () => {
    const { currentDelete } = this.state;
    invokeBackendApi({ path: `/users/${currentDelete}`, method: 'DELETE' })
      .then(() => {
        this.setState({ currentDelete: null });
        this.refresh();
      })
      .catch((e) => {
        console.log(e);
        alert(e.message);
      });
  };

  handleClickColumnHeader = (columnNumber) => {
    const ascending = !this.isSorted(columnNumber);
    const newSort = ascending ? columnNumber : -columnNumber;
    this.sort(newSort);
  }

  sort = (newSort) => {
    const { users } = this.state;
    const { displayedAttributes } = this.props;
    const comparedAttribute = displayedAttributes[Math.abs(newSort) - 1].attributeKey;
    const dessending = newSort < 0;
    const sortedUsers = users.slice().sort((a, b) => {
      if (dessending) [a, b] = [b, a];
      return (a.attr[comparedAttribute] || '')
        .localeCompare(b.attr[comparedAttribute] || '');
    });

    this.setState({ sortedUsers, sortColumn: newSort });
  };

  isSorted = (columnNumber) => {
    if (this.state.sortColumn === columnNumber) return true;
    else if (this.state.sortColumn === -columnNumber) return false;
    return undefined;
  };

  renderEditRow(user) {
    const { displayedAttributes } = this.props;
    const { currentEditUser } = this.state;
    return (
      <TableRow key={user.attr.sub}>
        {displayedAttributes.map(attr =>
            ((attr.editor && attr.editor.data && (attr.editor.updatable !== false || user.Username === 0)) ?
            <TableColumn key={attr.attributeKey}>
              <Autocomplete
                value={currentEditUser[attr.attributeKey]}
                {...attr.editor}
                onChange={e => this.handleOnChange(attr.attributeKey, e)}
                onAutocomplete={e => this.handleOnChange(attr.attributeKey, e)}
              />
            </TableColumn>
            : (attr.editor && attr.editor.menuItems && (attr.editor.updatable !== false || user.Username === 0)) ?
            <SelectFieldColumn
              key={attr.attributeKey}
              value={currentEditUser[attr.attributeKey]}
              {...attr.editor}
              sameWidth={false}
              onChange={e => this.handleOnChange(attr.attributeKey, e)}
            />
            : (attr.editor && (attr.editor.updatable !== false || user.Username === 0)) ?
            <EditDialogColumn
              key={attr.attributeKey}
              value={currentEditUser[attr.attributeKey]}
              {...attr.editor}
              onChange={e => this.handleOnChange(attr.attributeKey, e)}
              inlineIcon={null}
            />
            : <TableColumn grow={attr.grow} key={attr.attributeKey}>{attr.display ? attr.display(user.attr[attr.attributeKey], user) : user.attr[attr.attributeKey]}</TableColumn>))}
        <TableColumn style={{ minWidth: '120px' }} numeric key="actions">
          <Button icon className="md-inline-block" onClick={this.handleClickOnCancel}>cancel</Button>
          <LoaderButton
            icon
            onClick={() => this.handleSubmit()}
            disabled={!this.validateForm()}
            isLoading={this.state.isEditSubmitLoading}
            className="md-inline-block"
            text="done"
          />
        </TableColumn>
      </TableRow>
    );
  }

  renderUsersList() {
    const { displayedAttributes, intl } = this.props;
    const { currentEdit, sortedUsers, searchField } = this.state;

    const UserTableHeader = () => (
      <TableHeader>
        <TableRow>
          {[
            ...displayedAttributes.map((attr, i) => (
              <TableColumn
                sorted={this.isSorted(i + 1)}
                onClick={() => { this.handleClickColumnHeader(i + 1); }}
                grow={attr.grow}
                key={attr.attributeKey}
              >
                {attr.tableHeader}
              </TableColumn>)),
            <TableColumn key="kebab">{/* Kebab */}</TableColumn>,
          ]}

        </TableRow>
      </TableHeader>
    );

    const menuItems = user => [{
      leftIcon: <FontIcon>mode_edit</FontIcon>,
      primaryText: intl.formatMessage({ id: 'adminDashboard.users.edit' }),
      onClick: () => this.handleClickEdit(user),
    }, { divider: true }, {
      leftIcon: <FontIcon className="md-text--error">delete</FontIcon>,
      primaryText: <span className="md-text--error">{intl.formatMessage({ id: 'adminDashboard.users.delete' })}</span>,
      onClick: () => this.handleClickDelete(user),
    }];

    const TableBodyRow = ({ user }) => (
      <TableRow >
        {[...displayedAttributes.map(attr => (
          <TableColumn grow={attr.grow} key={attr.attributeKey} >
            {attr.display ? attr.display(user.attr[attr.attributeKey], user) : user.attr[attr.attributeKey]}
          </TableColumn>)),
          <KebabMenu
            key="kebab"
            menuItems={menuItems(user)}
            className="md-text-right"
          />,
        ]}

      </TableRow>
    );

    return (
      <DataTable plain className="bottom-padded">
        <UserTableHeader />
        <TableBody>{
          sortedUsers.filter(u => (
            latinize(u.attr.name || '').toLowerCase().includes(latinize(searchField.toLowerCase())) ||
            latinize(u.attr.family_name || '').toLowerCase().includes(latinize(searchField.toLowerCase())) ||
            latinize(u.attr.email || '').toLowerCase().includes(latinize(searchField.toLowerCase())) ||
            latinize(u.attr['custom:comment'] || '').toLowerCase().includes(latinize(searchField.toLowerCase())) ||
            latinize(u.attr['custom:imei'] || '').toLowerCase().includes(latinize(searchField.toLowerCase()))
          )).map(user =>
              (currentEdit === user.Username ? this.renderEditRow(user)
              : <TableBodyRow user={user} key={user.attr.sub} />
            ))
          }
        </TableBody>
      </DataTable>
    );
  }

  render() {
    const { intl, tabTitle } = this.props;
    const { currentDelete, searchField, error } = this.state;

    const actions = [{
      id: 'dialog-cancel',
      secondary: true,
      children: intl.formatMessage({ id: 'adminDashboard.users.deleteCancel' }),
      onClick: this.hide,
    }, {
      id: 'dialog-ok',
      primary: true,
      children: intl.formatMessage({ id: 'adminDashboard.users.deleteOk' }),
      onClick: this.confirmDelete,
    }];
    return (
      <div>
        <div className="md-grid">
          <h4 className="tableTitle">{tabTitle}</h4>
          <TextField
            block
            id="search-field"
            value={searchField}
            onChange={this.handleSearchFieldChange}
            placeholder={intl.formatMessage({ id: 'adminDashboard.users.search' })}
            className="md-cell--right md-cell--2 md-cell--middle "
            leftIcon={<FontIcon>search</FontIcon>}
          />
          <Button
            flat
            primary
            swapTheming
            iconChildren="add"
            onClick={() => this.handleClickNew()}
            className="md-cell--middle newButton"
          >{intl.formatMessage({ id: 'adminDashboard.users.new' })}
          </Button>
        </div>
        <div>
          <ErrorDisplay error={error} />
        </div>

        {!this.state.isLoading && this.renderUsersList()}

        <DialogContainer
          id="confirm-delete"
          title={intl.formatMessage({ id: 'adminDashboard.users.confirmDeleteTitle' })}
          visible={currentDelete !== null}
          actions={actions}
          onHide={this.hide}
          initialFocus="#dialog-cancel"
          contentClassName="md-grid"
        />
      </div>
    );
  }
});



// WEBPACK FOOTER //
// ./src/components/Users/UsersTab.js